import { _ as _extends } from './_rollupPluginBabelHelpers-aa9fecf9.js';
import React__default, { useRef, useState, useMemo, useEffect, useCallback, useContext } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import { c as commonPropTypes } from './commonPropTypes-c0ea5832.js';
import cx from 'classnames';
import { F as FormIntent } from './types-cb7b719f.js';
import { S as SvgError } from './error-b4cacae8.js';
import { S as SvgTickFilled } from './tick-filled-19977a65.js';

const {
  ERROR,
  SUCCESS,
  WARNING
} = FormIntent;
var ToastStatuses = {
  ERROR,
  SUCCESS,
  WARNING
};

var styles = {"toastContainer":"toast-module_toastContainer__3meJL","toast":"toast-module_toast__36K3j","notAutoDismissed":"toast-module_notAutoDismissed__NttbH","stickToast":"toast-module_stickToast__2vVnB","dismissed":"toast-module_dismissed__hLX9d","dismissedToast":"toast-module_dismissedToast__3H3wm","mobileToast":"toast-module_mobileToast__2ybWh","dismissedMobileToast":"toast-module_dismissedMobileToast__3Cjge","mobileStickToast":"toast-module_mobileStickToast__3PvA2","mobileAlign":"toast-module_mobileAlign__RD7a1","toastContent":"toast-module_toastContent__3Skeh","contentLongMessage":"toast-module_contentLongMessage__8K3AK","errorIcon":"toast-module_errorIcon__mnjTa","successIcon":"toast-module_successIcon__3yfDl","success":"toast-module_success__3vjz8","error":"toast-module_error__202Wu","warning":"toast-module_warning__2oAIj","text":"toast-module_text__1kwmJ","textLongMessage":"toast-module_textLongMessage__2tC9w","noIcon":"toast-module_noIcon__2293S","toastWrapper":"toast-module_toastWrapper__10k3T"};

const EXPIRATION_TIMEOUT = 7000;
const getIcon = (icon, status) => {
  if (icon) {
    return icon;
  }
  if (status === ToastStatuses.ERROR) {
    return /*#__PURE__*/React__default.createElement(SvgError, {
      className: styles.errorIcon
    });
  }
  if (status === ToastStatuses.SUCCESS) {
    return /*#__PURE__*/React__default.createElement(SvgTickFilled, {
      className: styles.successIcon
    });
  }
  return null;
};
const Toast = ({
  autoDismiss = true,
  children = null,
  className,
  icon = null,
  longMessage = false,
  onClick = () => null,
  status = ToastStatuses.SUCCESS,
  ...props
}) => {
  const removeRef = useRef();
  removeRef.current = onClick;
  const [dismissed, setDismissed] = useState(false);
  const correctIcon = useMemo(() => getIcon(icon, status), [icon, status]);
  useEffect(() => {
    if (!autoDismiss) {
      return null;
    }
    const id = setTimeout(() => {
      removeRef.current();
    }, EXPIRATION_TIMEOUT);
    return () => clearTimeout(id);
  }, [autoDismiss]);
  return /*#__PURE__*/React__default.createElement("div", {
    className: cx(styles.toastContainer, className, {
      [styles.dismissed]: dismissed,
      [styles.notAutoDismissed]: !autoDismiss
    })
  }, /*#__PURE__*/React__default.createElement("div", _extends({
    className: cx(styles.toastContent, {
      [styles.success]: status === ToastStatuses.SUCCESS,
      [styles.error]: status === ToastStatuses.ERROR,
      [styles.warning]: status === ToastStatuses.WARNING,
      [styles.contentLongMessage]: longMessage,
      [styles.noIcon]: !correctIcon
    }),
    onClick: () => setDismissed(true),
    role: "log"
  }, props), correctIcon && /*#__PURE__*/React__default.cloneElement(correctIcon, {
    className: cx(styles.mobileAlign, correctIcon.props?.className)
  }), /*#__PURE__*/React__default.createElement("div", {
    className: cx(styles.text, styles.mobileAlign, {
      [styles.textLongMessage]: longMessage
    })
  }, children)));
};
Toast.propTypes = {
  /**
   * Auto-removing of toast
   */
  autoDismiss: PropTypes.bool,
  /**
   * Information which will be shown on Toast
   */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.node, PropTypes.object]).isRequired,
  /**
   * ClassName
   */
  className: commonPropTypes.className,
  /**
   * Icon which will be displayed on Toast left side
   */
  icon: commonPropTypes.reactComponent,
  longMessage: PropTypes.bool,
  /**
   * Handler of click action in case when user want to remove toast immediately
   */
  onClick: PropTypes.func,
  /**
   * Sets Toast status (colour)
   */
  status: PropTypes.oneOf([ToastStatuses.SUCCESS, ToastStatuses.ERROR, ToastStatuses.WARNING])
};
var Toast$1 = Toast;

/* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-noninteractive-element-interactions */

const ToastContext = /*#__PURE__*/React__default.createContext(null);
ToastContext.displayName = 'ToastContext';
var ToastContext$1 = ToastContext;

// Create a random ID
// eslint-disable-next-line import/prefer-default-export
function generateUEID() {
  // eslint-disable-next-line no-bitwise
  let first = Math.random() * 46656 | 0;
  // eslint-disable-next-line no-bitwise
  let second = Math.random() * 46656 | 0;
  first = `000${first.toString(36)}`.slice(-3);
  second = `000${second.toString(36)}`.slice(-3);
  return first + second;
}

function useToastLogic() {
  const [toasts, setToasts] = useState([]);
  const add = useCallback((content, status = ToastStatuses.SUCCESS, props) => {
    const id = generateUEID();
    setToasts(values => [...values, {
      content,
      id,
      props,
      status
    }]);
    return id;
  }, []);
  const remove = useCallback(id => setToasts(toasts.filter(t => t.id !== id)), [toasts]);
  const providerValue = useMemo(() => ({
    add,
    remove
  }), [add, remove]);
  return {
    providerValue,
    remove,
    toasts
  };
}

const UseToastProvider = ({
  children = null,
  portal = null
}) => {
  const [mounted, setMounted] = useState(false);
  const {
    providerValue,
    remove,
    toasts
  } = useToastLogic();
  useEffect(() => setMounted(true), []);
  return /*#__PURE__*/React__default.createElement(ToastContext$1.Provider, {
    value: providerValue
  }, children, mounted || toasts.length ? /*#__PURE__*/createPortal( /*#__PURE__*/React__default.createElement("div", {
    className: styles.toastWrapper
  }, toasts.map(toast => {
    const {
      content,
      id,
      props,
      status
    } = toast;
    return /*#__PURE__*/React__default.createElement(Toast$1, _extends({
      key: id,
      onClick: () => remove(id),
      status: status
    }, props), content);
  })), portal || document.body) : null);
};
UseToastProvider.propTypes = {
  /**
   * Children - React children
   */
  children: commonPropTypes.children,
  /**
   * DOM element to open portal
   */
  portal: commonPropTypes.children
};
const withToastProvider = (Component, isSSR = false) => {
  const WithToastProvider = props => {
    const {
      providerValue,
      remove,
      toasts
    } = useToastLogic();
    return /*#__PURE__*/React__default.createElement(ToastContext$1.Provider, {
      value: providerValue
    }, /*#__PURE__*/React__default.createElement(Component, props), toasts.length || !isSSR ? /*#__PURE__*/createPortal( /*#__PURE__*/React__default.createElement("div", {
      className: styles.toastWrapper
    }, toasts.map(toast => /*#__PURE__*/React__default.createElement(Toast$1, {
      key: toast.id,
      onClick: () => remove(toast.id),
      status: toast.status
    }, toast.content))), document.body) : null);
  };
  return WithToastProvider;
};
var withToastProvider$1 = withToastProvider;

function useToast() {
  const context = useContext(ToastContext$1);
  return {
    add: context.add,
    remove: context.remove
  };
}

export { Toast$1 as T, UseToastProvider as U, ToastContext$1 as a, useToastLogic as b, ToastStatuses as c, useToast as u, withToastProvider$1 as w };
